import { db } from '../../db/indexeddb'
import {
  getCloudRewardUsages,
  getCloudRewardUsageIds,
  getCloudRewardUsagesByIds,
  getDeletedCloudRewardUsages,
  createCloudRewardUsage,
  updateCloudRewardUsage,
  deleteCloudRewardUsage,
} from '../../supabase/queries'
import { CloudRewardUsageSchema, CloudRewardUsagePayloadSchema } from '../sync-schemas'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { RewardUsage } from '../../db/models'

/* ── Reward usage ─────────────────────────────────────────── */

// "Zuletzt benutzt" in the reward picker. Standalone (no FK), one row per
// reward. `local_id` is the catalogue reward id, identical on every device, so
// two devices converge on the same row and LWW settles `usedAt` — no
// name-based dedup needed (unlike tags/projects).

export const rewardUsagePullDescriptor: PullDescriptor<any> = {
  entityName: 'rewardUsage',
  settingsKey: 'lastRewardUsageSyncTime',

  fetchRemote: (userId, since) => getCloudRewardUsages(userId, since),
  fetchRemoteIds: (userId) => getCloudRewardUsageIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudRewardUsagesByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudRewardUsages(userId, since),

  safeParse: (raw) => CloudRewardUsageSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.rewardUsages,
  getAllLocal: () => db.rewardUsages.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.rewardUsages.filter(u => !u.deletedAt).count()
    return count === 0
  },

  // The row id IS the catalogue reward id — never a random UUID. That is what
  // makes the same reward the same row on every device.
  buildLocalInsert: async (remote) => ({
    id: remote.local_id ?? remote.reward_id,
    rewardId: remote.reward_id,
    usedAt: new Date(remote.used_at),
    userId: remote.user_id,
    cloudId: remote.id,
    deletedAt: null,
    createdAt: new Date(remote.created_at),
    updatedAt: new Date(remote.updated_at),
    lastModified: new Date(remote.updated_at),
    synced: true,
    syncedAt: new Date(),
  }),

  buildLocalUpdate: async (remote) => ({
    rewardId: remote.reward_id,
    usedAt: new Date(remote.used_at),
    updatedAt: new Date(remote.updated_at),
  }),
}

export const rewardUsagePushDescriptor: PushDescriptor<RewardUsage> = {
  entityName: 'CloudRewardUsage',
  getLocal: (id) => db.rewardUsages.get(id).then(u => u ?? null),
  getLocalCloudId: (u) => u.cloudId,
  getLocalId: (u) => u.id,
  table: db.rewardUsages,

  buildCreatePayload: async (usage) => CloudRewardUsagePayloadSchema.parse({
    reward_id: usage.rewardId,
    used_at: usage.usedAt.toISOString(),
    local_id: usage.id,
  }),

  buildUpdatePayload: async (usage) => {
    const payload: Record<string, any> = CloudRewardUsagePayloadSchema.parse({
      reward_id: usage.rewardId,
      used_at: usage.usedAt.toISOString(),
      local_id: usage.id,
    })
    if (!usage.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudRewardUsage(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudRewardUsage(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudRewardUsage(cloudId, userId),
}
